import { Program } from '@citruscamps/citrus-client'
import { Interval } from 'luxon'
import { useEffect, useState } from 'react'
import { IDateMacroQuery } from '../../../interfaces/DateMacroQuery'
import { getIntervalByDateMacro, getIntervalByQuery } from '../../../utils/date-macro'

export const DefaultDateQueryWarningTimeout = 7500

interface IProps {
  program: Program
}

export interface DateQueryWarningProvider {
  isWarningShown: boolean
  transform: (value: IDateMacroQuery) => IDateMacroQuery
}

export const useDateQueryWarning = ({ program }: IProps): DateQueryWarningProvider => {
  const [isWarningShown, setIsWarningShown] = useState<boolean>(false)
  const [timeoutId, setTimeoutId] = useState<number>(0)

  useEffect(() => {
    if (isWarningShown && !timeoutId) {
      setTimeoutId(
        window.setTimeout(() => {
          setIsWarningShown(false)
          setTimeoutId(0)
        }, DefaultDateQueryWarningTimeout),
      )

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [isWarningShown, setTimeoutId, setIsWarningShown, timeoutId])

  const showWarning = () => {
    if (isWarningShown) return
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    setIsWarningShown(true)
  }

  const transform = (originalMacro: IDateMacroQuery): IDateMacroQuery => {
    const original: Interval = getIntervalByDateMacro(originalMacro, program)
    const adjusted: Interval = getIntervalByQuery(originalMacro, program)
    const hasRangeChanged =
      !!original.start &&
      !!original.end &&
      !!adjusted.start &&
      !!adjusted.end &&
      (!original.start.equals(adjusted.start) || !original.end.equals(adjusted.end))

    if (hasRangeChanged) {
      showWarning()
      return {
        date_macro: originalMacro.date_macro,
        unit: originalMacro.unit,
        start_date: adjusted.start.toJSDate(),
        end_date: adjusted.end.toJSDate(),
        timezone: originalMacro.timezone,
      }
    }

    return {
      date_macro: originalMacro.date_macro,
      unit: originalMacro.unit,
      start_date: originalMacro.start_date,
      end_date: originalMacro.end_date,
      timezone: originalMacro.timezone,
    }
  }

  return {
    isWarningShown,
    transform,
  }
}
